import { useState, useCallback } from 'react'

import Vehicle from '../interfaces/Vehicle'
import { useAuth } from '../hooks/useAuth'
import usePictureDelete from '../hooks/useImageDelete'
import useImagesReorder from '../hooks/useImagesReorder'
import useFileUpload, { UserOrganization } from '../hooks/useFileUpload'
import useImagesReorderAll from '../hooks/useImagesReorderAll'
import useImageSelection from '../hooks/useImageSelection'
import useImageBulkDownload from '../hooks/useImageBulkDownload'

import ImageGallery from './ImageGallery'
import ImageSelectionToolbar from './ImageSelectionToolbar'
import ConfirmationModal from './ConfirmationModal'
import { ButtonAction } from './Buttons'
import { Info } from './MessageInfo'

// Imagenes publicas, o de registro en la página de Vehiculos
const VehicleImages: React.FC<{
  vehicle: Vehicle
  canModify: boolean
  registry: boolean
  onRefresh: () => void
}> = ({ vehicle, canModify, registry, onRefresh }) => {
  // Para que el imageGallery se de cuenta de que hay cambio
  const [imageUrls, setImageUrls] = useState<string[]>(
    registry ? (vehicle.pictures?.registry ?? []) : (vehicle.pictures?.public ?? [])
  ) // Local state for image URLs
  const { userData } = useAuth()
  const { pictureDelete, errorPicture } = usePictureDelete()
  const [orderSaveDisabled, setOrderSaveDisabled] = useState<boolean>(true)
  const { imagesReorder, errorImage } = useImagesReorder()
  const { imagesReorderAll, errorImagesReorderAll } = useImagesReorderAll()
  const [infoMessage, setInfoMessage] = useState<string>('')
  const hayMasDeUna = imageUrls.length > 1

  // Bulk download and delete functionality
  const { downloadMultipleImages, downloadProgress, deleteMultipleImages, deleteProgress } = useImageBulkDownload()

  // State for delete confirmation
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState<boolean>(false)
  const [imagesToDelete, setImagesToDelete] = useState<string[]>([])

  // Bulk delete handler with confirmation
  const handleBulkDeleteRequest = useCallback((imageUrls: string[]) => {
    setImagesToDelete(imageUrls)
    setDeleteConfirmationOpen(true)
  }, [])

  // Image selection functionality (defined before handlers that use it)
  const imageSelection = useImageSelection({
    images: imageUrls,
    onBulkDownload: downloadMultipleImages,
    onBulkDelete: handleBulkDeleteRequest,
  })

  const handleBulkDeleteConfirm = useCallback(async () => {
    setDeleteConfirmationOpen(false)
    const result = await deleteMultipleImages(imagesToDelete, vehicle._id, registry, (deletedImageUrl) => {
      // Update local state immediately for each successful deletion
      setImageUrls((prevImages) => prevImages.filter((url) => url !== deletedImageUrl))
    })

    if (result) {
      // Call refresh callback after bulk operation completes
      onRefresh()

      // Clear selection after successful deletions
      if (result.success > 0) {
        imageSelection.resetSelection()
      }
    }

    setImagesToDelete([])
  }, [imagesToDelete, deleteMultipleImages, vehicle._id, registry, onRefresh, imageSelection])

  const handleBulkDeleteCancel = useCallback(() => {
    setDeleteConfirmationOpen(false)
    setImagesToDelete([])
  }, [])

  const userOrganization: UserOrganization = {
    organization_id: vehicle.organization,
    user_id: userData?.user?._id ?? '',
    vehicle_id: vehicle._id,
    registry: registry ? '1' : '0',
    attachment: '0',
  }
  const { isUploading, error, handleFileChangeEx } = useFileUpload(userOrganization, (newImageUrl?: string[]) => {
    if (newImageUrl) {
      // Callback to update image list after successful upload
      setImageUrls((prevImages) => {
        const updatedImages = [...prevImages, ...newImageUrl]

        return updatedImages
      })
      onRefresh()
    }
  })
  const onPictureDeleted = async (pictureId: string): Promise<void> => {
    await pictureDelete(pictureId, vehicle._id, registry, '')

    // lo sacamos de la lista
    if (!errorPicture) {
      setImageUrls((prevImages) => {
        const newImages = prevImages.filter((item) => item !== pictureId)

        return newImages
      })
      onRefresh()
    }
  }
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    await handleFileChangeEx(event)
  }

  const setImageUrlsEx = (imgs: string[]) => {
    setImageUrls(imgs)
    setOrderSaveDisabled(false)
  }

  // Guardamos el order de las imagenes
  const orderSave = async () => {
    const resp = await imagesReorder(imageUrls, vehicle._id, registry)

    if (resp) {
      setOrderSaveDisabled(true)
      setInfoMessage('Guardado el orden de las imágenes.')
    }
  }

  // Click en reordenar
  const reorderAllHandler = async () => {
    const urls = await imagesReorderAll(vehicle._id, registry)
    setImageUrls(urls)
    setOrderSaveDisabled(false)
  }

  return (
    <div className="flex flex-col items-center">
      {/* Bulk Selection Toolbar */}
      {imageUrls.length > 0 && (
        <div className="w-full max-w-6xl mb-4">
          <div className="flex justify-between items-center mb-4">
            {/* Image count indicator */}
            <div className="text-sm text-gray-500">
              {imageUrls.length} imagen{imageUrls.length !== 1 ? 'es' : ''}
            </div>
          </div>

          <ImageSelectionToolbar
            isSelectionMode={imageSelection.isSelectionMode}
            selectedCount={imageSelection.selectedCount}
            totalCount={imageSelection.totalCount}
            onToggleSelectionMode={imageSelection.toggleSelectionMode}
            onSelectAll={imageSelection.selectAll}
            onSelectNone={imageSelection.selectNone}
            onBulkDownload={imageSelection.handleBulkDownload}
            canDownload={imageSelection.canDownload}
            downloadProgress={downloadProgress}
            onBulkDelete={imageSelection.handleBulkDelete}
            canDelete={imageSelection.canDelete && canModify}
            showDeleteOption={canModify}
            deleteProgress={deleteProgress}
          />
        </div>
      )}

      {imageUrls.length > 0 ? (
        <ImageGallery
          onPictureDeleted={onPictureDeleted}
          canModify={canModify}
          imageUrls={imageUrls}
          setImageUrls={setImageUrlsEx}
          selectionMode={imageSelection.isSelectionMode}
          selectedImages={imageSelection.selectedImageUrls}
          onImageSelectionChange={imageSelection.handleSelectionChange}
        />
      ) : (
        <div className="flex flex-col items-center">
          <div className="font-bold text-xl">Recuerda agregar imágenes</div>
          <div>(máximo 50 MB cada vez)</div>
        </div>
      )}

      {canModify && (
        <div className="flex flex-col items-center space-y-4">
          {/* Custom styled button to replace input[type=file] */}
          <label
            htmlFor="file-upload"
            className={`block w-max px-4 py-2 cursor-pointer bg-green-500 text-white rounded hover:bg-green-600 ${
              isUploading && 'opacity-50 cursor-not-allowed'
            }`}
          >
            {isUploading ? 'Cargando...' : 'Agregar imagen...'}
          </label>
          <input
            id="file-upload"
            type="file"
            multiple={true}
            accept="image/*"
            onChange={handleFileChange}
            className="hidden" // Hide the default file input
          />

          <ButtonAction label="Guardar orden" handleButtonClick={orderSave} disabled={orderSaveDisabled} />
          {hayMasDeUna && (
            <div className="flex flex-col items-center gap-2 mt-4">
              <ButtonAction label="Ordenar imágenes por nombre" handleButtonClick={reorderAllHandler} />
              <div className="text-sm text-gray-600 text-center max-w-md">
                💡 Tip: Arrastra las imágenes para reordenar, haz clic en una imagen para verla en pantalla completa
              </div>
            </div>
          )}

          <div className="flex justify-center mt-4">
            <Info message={infoMessage} trigger={Date.now()} onTimeout={() => setInfoMessage('')} noCenter={true} />
          </div>
          {error && <p className="text-red-500">{error}</p>}
          {errorPicture && <p className="text-red-500">{errorPicture.message}</p>}
          {errorImage && <p className="text-red-500">{errorImage.message}</p>}
          <p className="text-red-500">{errorImagesReorderAll?.message}</p>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {deleteConfirmationOpen && (
        <ConfirmationModal
          message={`¿Estás seguro de eliminar ${imagesToDelete.length} imagen${imagesToDelete.length > 1 ? 'es' : ''}? Esta acción no se puede deshacer.`}
          onConfirm={handleBulkDeleteConfirm}
          onCancel={handleBulkDeleteCancel}
        />
      )}
    </div>
  )
}
export default VehicleImages
